// This program has been developed by students from the bachelor Computer Science at
// Utrecht University within the Software Project course.
// Copyright (c) Utrecht University (Department of Information and Computing Sciences)

import axios from "axios";
import * as types from "@/utils/Types";

/**
 * Gets the levels created by a user
 * @returns A list of levels
 */
async function getMyLevelsTeacher(): Promise<types.level[] | null> {
  try {
    const res = await axios.get<types.level[]>(`/Level/MyCreatedLevels`);
    return res.data;
  } catch (err) {
    console.error(err);
    return null;
  }
}

/**
 * Gets the levels which can be added to a bundle (all global and own levels)
 * @returns A list of levels
 */
async function getAddableLevels(): Promise<types.level[] | null> {
  try {
    const res = await axios.get<types.level[]>(`/Level/AddableLevels`);
    return res.data;
  } catch (err) {
    console.error(err);
    return null;
  }
}

/**
 * Gets a level using Teacher / Admin permissions
 * @param levelId The ID of the level you want to get
 * @returns A level
 */
async function getLevel(levelId: number): Promise<types.level | null> {
  try {
    const res = await axios.get<types.level>(`/Level/${levelId}`);
    return res.data;
  } catch (err) {
    console.error(err);
    return null;
  }
}

/**
 * Gets all published levels
 * @returns All published levels
 */
async function getPublishedLevels(): Promise<types.level[] | null> {
  try {
    const res = await axios.get<types.level[]>(`/Level/Published`);
    return res.data;
  } catch (err) {
    console.error(err);
    return null;
  }
}

/**
 * Gets the optimal solution for a level for the teacher.
 * @param levelId The ID of the level
 * @returns The optimal solution for a level
 */
async function getOptimalSolution(
  levelId: number
): Promise<types.levelSave | null> {
  try {
    const res = await axios.get<types.levelSave>(
      `/Level/OptimalSolution/${levelId}`
    );
    return res.data;
  } catch (err) {
    console.error(err);
    return null;
  }
}

/**
 * Requests that the level be published to Stichting Praktijkleren
 * and receives the mail information to go into a mailTo
 * @param levelId The ID of the level you want to publish
 * @returns A mailTo request
 */
async function requestPublicationLevel(
  levelId: number
): Promise<string | null> {
  try {
    const res = await axios.get<string>(`/Level/RequestPublication/${levelId}`);
    return res.data;
  } catch (err) {
    console.error(err);
    return null;
  }
}

/**
 * Tells the server to create a new level
 * @param worldName The name of the world
 * @param levelName The new name of the level that gets created
 * @returns a level
 */
async function createLevel(
  worldName: string,
  levelName: string
): Promise<types.levelInfo | null> {
  try {
    const res = await axios.post(
      `/Level/Create?world=${worldName}&name=${levelName}`
    );
    return res.data;
  } catch (err) {
    console.error(err);
    return null;
  }
}

/**
 * Tells the server to make a clone of a level
 * @param levelId The ID of the level you want to make a clone of
 * @returns The newly cloned level
 */
async function cloneLevel(levelId: number): Promise<types.level | null> {
  try {
    const res = await axios.post(`/Level/Clone/${levelId}`);
    return res.data;
  } catch (err) {
    console.error(err);
    return null;
  }
}

/**
 * Update a level's layout from the level editor
 * @param editorSave The current level layout from the level editor
 * @returns A HTTP status code
 */
async function updateLevel(
  editorSave: types.levelInfo
): Promise<number | null> {
  try {
    const res = await axios.put(`/Level/Update`, editorSave);
    return res.status;
  } catch (err) {
    console.error(err);
    return null;
  }
}

/**
 * Publish a level to be available for bundles
 * @param levelId The ID of the level you want to publish
 * @returns A HTTP status code
 */
async function publishLevel(levelId: number): Promise<number | null> {
  try {
    const res = await axios.put(`/Level/Publish/${levelId}`);
    return res.status;
  } catch (err) {
    console.error(err);
    return null;
  }
}

/**
 * Delete a level permanently everywhere
 * @param levelId The ID of the level you want to delete
 * @returns A HTTP status code
 */
async function deleteLevel(levelId: number): Promise<number | null> {
  try {
    const res = await axios.delete(`/Level/Delete/${levelId}`);
    return res.status;
  } catch (err) {
    console.error(err);
    return null;
  }
}

export {
  getMyLevelsTeacher,
  getAddableLevels,
  getLevel,
  getPublishedLevels,
  getOptimalSolution,
  requestPublicationLevel,
  createLevel,
  cloneLevel,
  updateLevel,
  publishLevel,
  deleteLevel,
};
